import type { AnswerRefAlias } from '@agavi/blocks';

import { driverStandInKey } from '@/components/task/answer-ref-context';


/**
 * EVERY BENCH KEY THAT NAMES THE SAME SOURCE BLOCK — so a family cycles in unison.
 *
 * A stand-in is stored per KEY and a key is an alias or a driver address, while the answer
 * it holds is the source BLOCK's whole answer. A Numerical answers with a value, an
 * uncertainty and a unit, and those are three aliases over one block: set one to the exact
 * answer and another to a clear mistake and the sentence reads a value from one draw beside
 * an uncertainty from a different one. The owner: "if it's part of a family, like the number,
 * then all should cycle in unison, otherwise you end up with text disconnected from the
 * answer."
 *
 * So the writer fans out. One pick, one answer, written to every key that names the block it
 * came from — which is also what keeps a chip and the DRIVER entry in step when the driver is
 * the same block the chip quotes.
 *
 * By BLOCK and not by alias, because the family is the block: two aliases over one Numerical
 * are the same answer read two ways, and a third alias over a different block is not in it
 * however it is named.
 */
export function familyKeysFor(
  key: string,
  aliases: readonly AnswerRefAlias[],
): string[] {
  /* Which blocks this key speaks for. An alias may hold several — the equivalence case —
     and an address holds exactly one. */
  const blocks = new Set<string>();
  const alias = aliases.find(a => a.id === key);
  if (alias) for (const m of alias.members) blocks.add(driverStandInKey(m.taskId, m.blockId));
  else blocks.add(key);
  if (blocks.size === 0) return [key];

  const out = new Set<string>([key, ...blocks]);
  for (const a of aliases) {
    if (a.members.some(m => blocks.has(driverStandInKey(m.taskId, m.blockId)))) out.add(a.id);
  }
  return [...out];
}
